// src/utils/cookies.ts

import { DEFAULT_COOKIE_EXPIRY_DAYS } from "./config";

/**
 * Sets a cookie with the specified name, value, and expiration time.
 * Defaults to DEFAULT_COOKIE_EXPIRY_DAYS if no expiration unit is provided.
 *
 * @param name - The name of the cookie.
 * @param value - The value of the cookie.
 * @param expireIn - The duration until the cookie expires.
 * @param expireUnit - The unit of time for the expiration duration ('hours', 'minutes', 'days'). Defaults to 'days'.
 * @param sameSite - The SameSite attribute value ('Strict', 'Lax', 'None'). Defaults to 'Lax'.
 * @param secure - Whether to include the Secure attribute. Defaults to true.
 */
export function setCookie(
	name: string,
	value: string,
	expireIn: number,
	expireUnit: "hours" | "minutes" | "days" = "days",
	sameSite: "Strict" | "Lax" | "None" = "Lax",
	secure: boolean = true
): void {
	let expires = "";
	const date = new Date();
	let durationMs = 0;

	switch (expireUnit) {
		case "hours":
			durationMs = expireIn * 60 * 60 * 1000;
			break;
		case "minutes":
			durationMs = expireIn * 60 * 1000;
			break;
		case "days":
		default: // Default to days if unit is invalid or not provided
			// Handle negative expireIn for deletion
			if (expireIn < 0) {
				durationMs = -1 * 24 * 60 * 60 * 1000; // Set expiry in the past
			} else {
				durationMs = (expireIn || DEFAULT_COOKIE_EXPIRY_DAYS) * 24 * 60 * 60 * 1000;
			}
			break;
	}

	if (durationMs !== 0) {
		date.setTime(date.getTime() + durationMs);
		expires = "; expires=" + date.toUTCString();
	}

	const sameSiteAttribute = `; SameSite=${sameSite}`;
	const secureAttribute = secure ? "; Secure" : "";

	document.cookie = `${name}=${
		value || ""
	}${expires}; path=/${sameSiteAttribute}${secureAttribute}`;
}

/**
 * Retrieves the value of a cookie by name.
 * @param cname - The name of the cookie.
 * @returns The value of the cookie, or an empty string if it doesn't exist.
 */
export function getCookie(cname: string): string {
	const name = cname + "=";
	try {
		const decodedCookie = decodeURIComponent(document.cookie);
		const ca = decodedCookie.split(";");
		for (let i = 0; i < ca.length; i++) {
			let c = ca[i];
			while (c.charAt(0) === " ") {
				c = c.substring(1);
			}
			if (c.indexOf(name) === 0) {
				return c.substring(name.length, c.length);
			}
		}
	} catch (error) {
		console.error("Error decoding cookie:", error);
		// Fallback or specific error handling can be added here
	}
	return "";
}

/**
 * Deletes a cookie by setting its expiration date to the past.
 * @param name - The name of the cookie to delete.
 */
export function deleteCookie(name: string): void {
	setCookie(name, "", -1, "days"); // Set expiry in the past
}

/**
 * Checks if a cookie exists.
 * @param name - The name of the cookie to check.
 * @returns True if the cookie exists, false otherwise.
 */
export function cookieExists(name: string): boolean {
	return getCookie(name) !== "";
}
